import * as React from 'react';

import {
  Badge,
  Button,
  cn,
  Col,
  Row,
  SliderField,
  Switch,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideMonitorSmartphone,
  LucidePlug,
  LucideUnplug,
  LucideWrench,
} from 'lucide-react';

import { FormSection } from '../../_shared/FormSection';
import { SettingsContent } from '../../_shared/SettingsContent';

import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { $user } from '~/shared/providers/auth/identity.localState';
import { useUStudio } from '~/shared/providers/ustudio';

const ustudioPreferencesQueryAtom = createQueryAtomFactory(
  'settings.ustudioPreferences',
  (params: { userId: string }) => ({
    userPreferences: {
      __args: { userId: params.userId },
      ustudioEnabled: true,
      ustudioBrightness: true,
    },
  }),
);

const updatePreferencesMutation = createMutationAtomFactory(
  'settings.ustudioPreferencesUpdate',
  (params: {
    userId: string;
    ustudioEnabled?: boolean;
    ustudioBrightness?: number;
  }) => ({
    coreUpdateUserPreferences: {
      __args: {
        input: {
          userId: params.userId,
          ustudioEnabled: params.ustudioEnabled,
          ustudioBrightness: params.ustudioBrightness,
        },
      },
      ustudioEnabled: true,
      ustudioBrightness: true,
    },
  }),
);

const $ustudioPrefs = ustudioPreferencesQueryAtom((get) => {
  const u = get($user);
  return { variables: { userId: u?.id ?? '' }, enabled: !!u?.id };
});

const $ustudioMutate = updatePreferencesMutation({});

interface LogEntry {
  time: string;
  message: string;
}

const STATUS_COLORS: Record<string, string> = {
  disconnected: 'bg-neutral/30',
  connecting: 'bg-yellow-500',
  connected: 'bg-green-500',
  error: 'bg-red-500',
};

export const UStudioView = () => {
  const t = useTranslations();
  const user = useAtomValue($user);
  const ustudio = useUStudio();

  const prefs = useAtomValue($ustudioPrefs);

  const [enabled, setEnabled] = React.useState(false);
  const [brightness, setBrightness] = React.useState(128);
  const [isSaving, setIsSaving] = React.useState(false);
  const [eventLog, setEventLog] = React.useState<LogEntry[]>([]);
  const [debugOpen, setDebugOpen] = React.useState(false);

  const mutate = useSetAtom($ustudioMutate);

  const webHidAvailable = 'hid' in navigator;

  const isDirty = React.useMemo(() => {
    const data = prefs.data?.userPreferences;
    if (!data) return false;
    return (
      enabled !== (data.ustudioEnabled as boolean) ||
      brightness !== (data.ustudioBrightness as number)
    );
  }, [enabled, brightness, prefs.data]);

  const addLogEntry = React.useCallback((message: string) => {
    setEventLog((prev) => {
      const entry: LogEntry = {
        time: new Date().toLocaleTimeString(),
        message,
      };
      return [entry, ...prev].slice(0, 50);
    });
  }, []);

  const handleSave = React.useCallback(async () => {
    if (!user?.id) return;
    setIsSaving(true);
    try {
      await mutate({
        userId: user.id,
        ustudioEnabled: enabled,
        ustudioBrightness: brightness,
      });
      prefs.refetch?.();
      if (ustudio.agent) {
        await ustudio.agent.setBrightness(brightness);
      }
    } finally {
      setIsSaving(false);
    }
  }, [user?.id, enabled, brightness, mutate, prefs, ustudio.agent]);

  const handleDiscard = React.useCallback(() => {
    const data = prefs.data?.userPreferences;
    if (data) {
      setEnabled(data.ustudioEnabled as boolean);
      setBrightness(data.ustudioBrightness as number);
    }
  }, [prefs.data]);

  const handleConnect = React.useCallback(async () => {
    addLogEntry('Connecting...');
    try {
      await ustudio.connect();
      addLogEntry('Connected');
    } catch (err) {
      addLogEntry(`Connection failed: ${err}`);
    }
  }, [ustudio, addLogEntry]);

  const handleDisconnect = React.useCallback(async () => {
    await ustudio.disconnect();
    addLogEntry('Disconnected');
  }, [ustudio, addLogEntry]);

  React.useEffect(() => {
    const data = prefs.data?.userPreferences;
    if (data) {
      setEnabled(data.ustudioEnabled as boolean);
      setBrightness(data.ustudioBrightness as number);
    }
  }, [prefs.data]);

  React.useEffect(() => {
    if (ustudio.agent && ustudio.status === 'connected') {
      ustudio.agent.setBrightness(brightness).catch(() => undefined);
    }
  }, [brightness, ustudio.agent, ustudio.status]);

  React.useEffect(() => {
    if (ustudio.error) {
      addLogEntry(`Error: ${ustudio.error}`);
    }
  }, [ustudio.error, addLogEntry]);

  const statusLabel = t.ustudioView.status[ustudio.status] ?? ustudio.status;

  return (
    <Col className="@UStudioView h-full">
      <Col gap6 className="h-0 grow overflow-y-auto">
        <SettingsContent size="medium" className="gap-6 p-5">
          <FormSection
            icon={LucideMonitorSmartphone}
            title={t.ustudioView.preferences.title}
          >
            <Col gap4>
              <Row className="items-center justify-between">
                <span className="text-sm">
                  {t.ustudioView.preferences.enabled}
                </span>
                <Switch
                  isSelected={enabled}
                  onChange={setEnabled}
                  aria-label={t.ustudioView.preferences.enabled}
                />
              </Row>

              <SliderField
                label={t.ustudioView.preferences.brightness}
                minValue={0}
                maxValue={255}
                value={brightness}
                onChange={(v: number | number[]) =>
                  setBrightness(Array.isArray(v) ? v[0] : v)
                }
              />

              <Row gap2 className="justify-end">
                <Button
                  variant="outline"
                  size="small"
                  onPress={handleDiscard}
                  isDisabled={!isDirty || isSaving}
                >
                  {t.ustudioView.preferences.discard}
                </Button>
                <Button
                  variant="solid"
                  size="small"
                  onPress={handleSave}
                  isDisabled={!isDirty || isSaving}
                >
                  {t.ustudioView.preferences.save}
                </Button>
              </Row>
            </Col>
          </FormSection>

          <FormSection icon={LucideWrench} title={t.ustudioView.debug.title}>
            <Col gap4>
              {!webHidAvailable && (
                <div className="rounded-md border border-warning/20 bg-warning/5 px-3 py-2 text-sm text-warning/80">
                  {t.ustudioView.debug.webUsbUnavailable}
                </div>
              )}

              <Row className="items-center gap-3">
                <span className="text-sm">{t.ustudioView.debug.status}</span>
                <Row className="items-center gap-2">
                  <div
                    className={cn(
                      'size-2.5 rounded-full',
                      STATUS_COLORS[ustudio.status],
                    )}
                  />
                  <Badge size="small">{statusLabel}</Badge>
                </Row>
              </Row>

              <Row gap2>
                {ustudio.status !== 'connected' ? (
                  <Button
                    variant="solid"
                    size="small"
                    onPress={handleConnect}
                    isDisabled={
                      !webHidAvailable || ustudio.status === 'connecting'
                    }
                  >
                    <LucidePlug className="size-3.5" />
                    {t.ustudioView.debug.connect}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="small"
                    onPress={handleDisconnect}
                  >
                    <LucideUnplug className="size-3.5" />
                    {t.ustudioView.debug.disconnect}
                  </Button>
                )}
              </Row>

              {ustudio.deviceInfo && (
                <div className="rounded-md border border-neutral/15 px-3 py-2">
                  <Col gap1>
                    <span className="text-xs font-medium">
                      {t.ustudioView.debug.deviceInfo}
                    </span>
                    <span className="text-xs text-neutral/60">
                      {ustudio.deviceInfo.buttons} {t.ustudioView.debug.buttons}{' '}
                      &middot; {t.ustudioView.debug.framebuffer}{' '}
                      {ustudio.deviceInfo.fbWidth}x{ustudio.deviceInfo.fbHeight}
                    </span>
                  </Col>
                </div>
              )}

              <Button
                variant="ghost"
                size="small"
                className="justify-start gap-1 text-xs text-neutral/50 hover:text-neutral/80"
                onPress={() => setDebugOpen(!debugOpen)}
              >
                {debugOpen ? '\u25BE' : '\u25B8'} {t.ustudioView.debug.eventLog}
              </Button>

              {debugOpen && (
                <div className="max-h-[200px] overflow-y-auto rounded-md border border-neutral/15 bg-neutral/5 p-2 font-mono text-xs">
                  {eventLog.length === 0 && (
                    <span className="text-neutral/40">
                      {t.ustudioView.debug.noEvents}
                    </span>
                  )}
                  {eventLog.map((entry, i) => (
                    <div key={i} className="py-0.5">
                      <span className="text-neutral/40">{entry.time}</span>{' '}
                      {entry.message}
                    </div>
                  ))}
                </div>
              )}
            </Col>
          </FormSection>
        </SettingsContent>
      </Col>
    </Col>
  );
};
